import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import FutureDateRangePicker from 'components/common/DateRangePicker/FutureDateRangePicker';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  DeviceAvailabilityRecord,
  DeviceUnitLevel,
  DeviceUnitRecord
} from 'core/deviceManagement/DeviceManagement';
import {
  DEVICE_UNIT_KEY_FIELD,
  resolveDeviceUnitLevel
} from './deviceListColumns';
import { buildAvailabilityColumns } from './availabilityColumns';
import {
  AVAILABILITY_DATE_FORMAT,
  countDaysInRange
} from './availabilityFormat';
import { FutureAvailabilityModel } from './FutureAvailabilityModel';
import { AvailabilitySummaryCards } from './AvailabilitySummaryCards';
import { AvailabilityCalendarPanel } from './AvailabilityCalendarPanel';
import { PmpBookingTable } from './PmpBookingTable';
import { AvailabilityDaypartsField } from './AvailabilityDaypartsField';
import { DeviceDetailPanel } from './DeviceDetailPanel';
import { DeviceExportModal } from './DeviceExportModal';
import { DeviceListToolbar } from './DeviceListToolbar';
import styles from './deviceManagement.module.scss';

type FutureAvailabilityProps = {
  salesChannel?: number;
  channelDeliveryUnit?: DeviceUnitLevel;
  model: FutureAvailabilityModel;
  onOpenImport: () => void;
  /** 名單模式的檔名，匯出面板要把它寫進條件摘要。 */
  listFileName?: string;
};

/**
 * 機台可售狀況。
 *
 * 走期與投放時段是這一頁自己的條件，渠道與四個篩選條件則由頁首共用；分頁、排序、
 * 搜尋與清單同一套，全部在後端做。
 *
 * 操作列（匯入名單、搜尋、匯出清單）畫在查詢條件之上，與機台清單檢視同一個位置，
 * 用的也是同一支 `DeviceListToolbar`。
 */
export const FutureAvailability: React.FC<FutureAvailabilityProps> = ({
  salesChannel,
  channelDeliveryUnit,
  model,
  onOpenImport,
  listFileName
}) => {
  const {
    loading,
    records,
    summary,
    totalCount,
    failed,
    request,
    pagination,
    defaultSorted,
    dateRangeBounds,
    searchString,
    onDateRangeChange,
    onDaypartsChange,
    onTableChange,
    onHandleSearch,
    exporting,
    exportUnits
  } = model;
  const noChannel = salesChannel === undefined;
  const [detailUnitKey, setDetailUnitKey] = useState<string>();
  const [showExport, setShowExport] = useState(false);

  // 換渠道後開著的那一筆屬於上一個渠道。
  useEffect(() => {
    setDetailUnitKey(undefined);
  }, [salesChannel]);

  const onShowDetail = useCallback((record: DeviceUnitRecord) => {
    setDetailUnitKey(record.unitKey);
  }, []);

  const onOpenExport = useCallback(() => setShowExport(true), []);

  const unitLevel = useMemo(
    () => resolveDeviceUnitLevel(records, channelDeliveryUnit),
    [records, channelDeliveryUnit]
  );

  const columns = useMemo(
    () => buildAvailabilityColumns(unitLevel, onShowDetail),
    [unitLevel, onShowDetail]
  );

  const detailRecord = useMemo(
    () => records.find((record) => record.unitKey === detailUnitKey),
    [records, detailUnitKey]
  );

  const dayCount = countDaysInRange(request.startDate, request.endDate);

  // 詳情底下接兩塊：月曆與 PMP 購買記錄。兩塊都吃走期，所以只有這一頁掛得上。
  const renderAvailabilityDetail = (record: DeviceAvailabilityRecord) =>
    salesChannel === undefined ? null : (
      <>
        <AvailabilityCalendarPanel
          channelId={salesChannel}
          unitKey={record.unitKey}
          businessHoursProvenance={record.businessHoursProvenance}
          startDate={request.startDate}
          endDate={request.endDate}
          dayparts={request.dayparts}
          availabilityRate={record.availabilityRate}
        />
        <PmpBookingTable
          channelId={salesChannel}
          unitKey={record.unitKey}
          startDate={request.startDate}
          endDate={request.endDate}
        />
      </>
    );

  return (
    <div className={styles.tabContent}>
      {loading && <LoadingIndicator />}
      <DeviceListToolbar
        defaultSearchString={searchString}
        onSearch={onHandleSearch}
        onOpenImport={onOpenImport}
        onOpenExport={onOpenExport}
        disabled={noChannel}
      />
      <section className={styles.availabilitySection}>
        <div className={styles.availabilityField}>
          <label className={styles.filterTitle} htmlFor='flightRange'>
            {i18n.t('deviceManagement.labels.flightRange')}
          </label>
          <div className={styles.flightRange}>
            <FutureDateRangePicker
              placement='bottom-start'
              startDate={request.startDate}
              endDate={request.endDate}
              minDate={dateRangeBounds.minDate}
              maxDate={dateRangeBounds.maxDate}
              format={AVAILABILITY_DATE_FORMAT}
              showTimePicker={false}
              onChange={onDateRangeChange}
            />
            <span
              className={styles.flightDayCount}
              data-testid='flight-day-count'
            >
              {i18n.t('deviceManagement.labels.flightDayCount', {
                days: dayCount
              })}
            </span>
          </div>
        </div>
        <div className={styles.availabilityField}>
          <div className={styles.filterTitle}>
            {i18n.t('deviceManagement.labels.dayparts')}
          </div>
          {/* 提示要排在格線下方，所以與格線包在同一層；分開放會成為這一橫列的
              另一個項目，被排到格線右邊。 */}
          <div className={styles.daypartsColumn}>
            <AvailabilityDaypartsField
              value={request.dayparts}
              onChange={onDaypartsChange}
            />
            {/* 時段是對整份清單下的條件，而每個點位的營業時間各不相同，所以選到的
                小時可能落在某些點位的營業時間外；那些小時不會算進可售秒數。 */}
            <div
              className={styles.daypartsHint}
              data-testid='dayparts-business-hours-hint'
            >
              {i18n.t('deviceManagement.hints.daypartsOutsideBusinessHours')}
            </div>
          </div>
        </div>
        <AvailabilitySummaryCards summary={summary} unitLevel={unitLevel} />
      </section>
      <StickableTanstackTable<DeviceAvailabilityRecord>
        customPadding={false}
        remote
        keyField={DEVICE_UNIT_KEY_FIELD}
        data={records}
        columns={columns}
        // 三種零列各有各的下一步，理由與機台清單檢視那一支相同（見 DeviceList）。
        noDataIndication={i18n.t(
          noChannel
            ? 'deviceManagement.messages.selectChannelFirst'
            : failed
              ? 'deviceManagement.messages.listLoadFailed'
              : 'deviceManagement.messages.noUnit'
        )}
        defaultSorted={defaultSorted}
        onTableChange={onTableChange}
        pagination={pagination}
      />
      {detailRecord && salesChannel !== undefined && (
        <DeviceDetailPanel
          channelId={salesChannel}
          record={detailRecord}
          onClose={() => setDetailUnitKey(undefined)}
          extraContent={renderAvailabilityDetail(detailRecord)}
        />
      )}
      {showExport && (
        <DeviceExportModal
          totalCount={totalCount}
          sortField={request.sortField}
          sortOrder={request.sortOrder}
          filter={request.filter}
          search={request.search}
          listFileName={listFileName}
          exporting={exporting}
          onExport={exportUnits}
          onClose={() => setShowExport(false)}
          // 匯出走的是清單那一支端點，檔案裡沒有走期與可售統計那幾欄。
          hint={i18n.t('deviceManagement.hints.exportListColumnsOnly')}
        />
      )}
    </div>
  );
};
